/**
 * PetrifiedVine — a stone-hard vine choking the Master Mist Regulator in
 * the Overgrown Conservatory (Level 2).
 *
 * It coils around the regulator's feed pipe between the two handles and
 * seizes both of them: Basil's paws can't turn a handle through stone.
 * Only Resonant Drop — Basil's ground-slam and Minna's purr together —
 * shatters it, so the first thing the cats do in the new biome is use the
 * co-op power Elder Kitty gave them.
 *
 * Unlike RustedBlockage it has no collider: it chokes a mechanism, not a
 * path, so level traversal is unchanged. `shatter()` bursts it into stone
 * chips and emits BLOCKAGE_SHATTERED (the shared shatter SFX cue);
 * `shatter({ instant: true })` removes it silently for force-solve / seed.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { ParticleSystem } from "@babylonjs/core/Particles/particleSystem";
import { Animation } from "@babylonjs/core/Animations/animation";
import "@babylonjs/core/Animations/animatable";
import { InteractiveObject, InteractiveConfig } from "./InteractiveObject";
import { gameEvents } from "../../core/EventBus";
import { GameEvents } from "../../config/events";

export interface PetrifiedVineConfig extends Omit<InteractiveConfig, "size"> {
  /** Coil span along x (world units). Default 2.6 — both handles. */
  span?: number;
}

const STONE = new Color3(0.42, 0.45, 0.4);
const STONE_DARK = new Color3(0.27, 0.3, 0.26);
const MOSS_GLOW = new Color3(0.05, 0.09, 0.05);

export class PetrifiedVine extends InteractiveObject {
  private shattered = false;
  private readonly parts: Mesh[] = [];
  private readonly mats: StandardMaterial[] = [];
  private particles: ParticleSystem | null = null;

  constructor(scene: Scene, config: PetrifiedVineConfig) {
    const span = config.span ?? 2.6;
    super(scene, { ...config, size: new Vector3(span, 1.6, 0.8) });

    const stone = new StandardMaterial(`vine_${config.key}_stone`, scene);
    stone.diffuseColor = STONE;
    stone.specularColor = new Color3(0.08, 0.08, 0.08);
    stone.emissiveColor = MOSS_GLOW;
    const dark = new StandardMaterial(`vine_${config.key}_dark`, scene);
    dark.diffuseColor = STONE_DARK;
    dark.specularColor = Color3.Black();
    dark.emissiveColor = MOSS_GLOW.scale(0.6);
    this.mats.push(stone, dark);

    // The trunk: a thick stone stem lying along the feed pipe.
    const trunk = MeshBuilder.CreateCylinder(
      `vine_${config.key}_trunk`,
      { height: span, diameterTop: 0.22, diameterBottom: 0.34, tessellation: 10 },
      scene,
    );
    trunk.rotation.z = Math.PI / 2;
    this.addPart(trunk, dark, new Vector3(0, 0, 0));

    // Coils wrapped around the pipe, alternating tilt so they read as a
    // spiral rather than a stack of rings.
    const coils = Math.max(3, Math.round(span / 0.55));
    for (let i = 0; i < coils; i++) {
      const coil = MeshBuilder.CreateTorus(
        `vine_${config.key}_coil_${i}`,
        { diameter: 0.62 - (i % 2) * 0.08, thickness: 0.11, tessellation: 14 },
        scene,
      );
      coil.rotation.z = Math.PI / 2;
      coil.rotation.x = (i % 2 === 0 ? 1 : -1) * 0.45;
      this.addPart(coil, stone, new Vector3(-span / 2 + (i + 0.5) * (span / coils), 0, 0));
    }

    // Tendrils climbing up each handle's side and a few stone thorns.
    for (const [i, side] of [-1, 1].entries()) {
      const tendril = MeshBuilder.CreateCylinder(
        `vine_${config.key}_tendril_${i}`,
        { height: 1.3, diameterTop: 0.06, diameterBottom: 0.16, tessellation: 8 },
        scene,
      );
      tendril.rotation.z = side * 0.35;
      this.addPart(tendril, stone, new Vector3(side * (span / 2 - 0.15), 0.55, 0.12));
    }
    for (let i = 0; i < 4; i++) {
      const thorn = MeshBuilder.CreateCylinder(
        `vine_${config.key}_thorn_${i}`,
        { height: 0.26, diameterTop: 0, diameterBottom: 0.1, tessellation: 6 },
        scene,
      );
      thorn.rotation.z = (i % 2 === 0 ? 1 : -1) * 0.9;
      this.addPart(thorn, dark, new Vector3(-span / 2 + 0.4 + i * ((span - 0.8) / 3), 0.24, 0.16));
    }
  }

  private addPart(mesh: Mesh, mat: StandardMaterial, offset: Vector3): void {
    mesh.parent = this.rootMesh;
    mesh.position = offset;
    mesh.material = mat;
    mesh.isPickable = false;
    this.parts.push(mesh);
  }

  override getHighlightTarget(): Mesh {
    return this.parts[0] ?? this.rootMesh;
  }

  get isShattered(): boolean {
    return this.shattered;
  }

  /**
   * Shatter the vine. With `instant`, the parts are simply hidden and no
   * event fires (force-solve / QA seed); otherwise stone chips burst, the
   * coils fade over ~0.5 s and BLOCKAGE_SHATTERED fires with this key.
   */
  shatter({ instant = false }: { instant?: boolean } = {}): void {
    if (this.shattered) return;
    this.shattered = true;
    this.state = "solved";

    if (instant) {
      for (const part of this.parts) part.setEnabled(false);
      return;
    }

    try {
      const ps = new ParticleSystem(`vineShatter_${this.key}`, 36, this.scene);
      ps.createPointEmitter(new Vector3(-1.5, 0.5, -0.6), new Vector3(1.5, 2.5, 0.6));
      ps.emitter = this.rootMesh.position.clone();
      ps.minLifeTime = 0.4;
      ps.maxLifeTime = 1.0;
      ps.minSize = 0.08;
      ps.maxSize = 0.24;
      ps.minEmitPower = 2;
      ps.maxEmitPower = 6;
      ps.color1 = new Color4(STONE.r, STONE.g, STONE.b, 1);
      ps.color2 = new Color4(0.55, 0.62, 0.45, 1);
      ps.colorDead = new Color4(0.2, 0.22, 0.18, 0);
      ps.gravity = new Vector3(0, -9, 0);
      ps.emitRate = 0;
      ps.manualEmitCount = 36;
      ps.targetStopDuration = 1.2;
      ps.disposeOnStop = true;
      ps.start();
      this.particles = ps;
    } catch {
      // NullEngine / headless: no particle shaders — the gameplay effect
      // (the vine is gone) does not depend on the burst.
      this.particles = null;
    }

    const frames = 15; // ~0.5 s at 30 fps
    for (const part of this.parts) {
      const fade = new Animation(
        `vineFade_${part.name}`,
        "visibility",
        30,
        Animation.ANIMATIONTYPE_FLOAT,
        Animation.ANIMATIONLOOPMODE_CONSTANT,
      );
      fade.setKeys([
        { frame: 0, value: 1 },
        { frame: frames, value: 0 },
      ]);
      part.animations = [fade];
      this.scene.beginAnimation(part, 0, frames, false, 1, () => part.setEnabled(false));
    }

    gameEvents.emit(GameEvents.BLOCKAGE_SHATTERED, { key: this.key });
  }

  override dispose(): void {
    this.particles?.dispose();
    this.particles = null;
    for (const mat of this.mats) mat.dispose();
    // Base teardown disposes the root mesh and, with it, every part.
    super.dispose();
  }
}
